/** Real measurements on recorded / live audio: peaks, loudness, noise floor, clipping, click onsets. */
export const toDb = (x) => (x > 0 ? 20 * Math.log10(x) : -120);
const round = (v) => Math.round(v * 10) / 10;

/** Peak + RMS (dBFS) of a Float32 analyser frame. */
export function frameLevels(frame) {
  let peak = 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i += 1) {
    const v = Math.abs(frame[i]);
    if (v > peak) peak = v;
    sum += frame[i] * frame[i];
  }
  return { peakDb: toDb(peak), rmsDb: toDb(Math.sqrt(sum / frame.length)), clip: peak >= 0.98 };
}

/** Per-window peak across channels (50 ms windows) + overall peak and clipped-sample count. */
function windows(buffer, sec = 0.05) {
  const chs = Array.from({ length: buffer.numberOfChannels }, (_, c) => buffer.getChannelData(c));
  const win = Math.max(1, Math.round(buffer.sampleRate * sec));
  const rms = [];
  let peak = 0;
  let clipped = 0;
  for (let i = 0; i < buffer.length; i += win) {
    const end = Math.min(buffer.length, i + win);
    let s = 0;
    for (let j = i; j < end; j += 1) {
      let v = 0;
      for (const d of chs) { const x = Math.abs(d[j]); if (x > v) v = x; }
      if (v > peak) peak = v;
      if (v >= 0.989) clipped += 1;
      s += v * v;
    }
    rms.push(Math.sqrt(s / (end - i)));
  }
  return { rms, peak, clipped };
}

export const ISSUE_TEXT = {
  silent: { level: "bad", title: "No sound was recorded", tip: "Check the right microphone is chosen and not muted, then record again." },
  clipping: { level: "bad", title: "Too loud — it distorted", tip: "Step back a little or turn the mic's input gain down, then record again." },
  quiet: { level: "warn", title: "Very low volume", tip: "Move closer to the mic (a hand's width away) or raise the input gain." },
  noise: { level: "warn", title: "Background noise", tip: "Turn off fans or air-con, close windows, and record in a softer room." },
  sync: { level: "warn", title: "Timing may be off", tip: "Part of the take may be missing. Close other heavy tabs and record again." },
  uncalibrated: { level: "info", title: "Timing not calibrated", tip: "Run Sync calibration in Setup for the tightest line-up with the music." },
};

/**
 * Check a finished take (never changes it). → { peak_db, rms_db, noise_db, clipped, issues: [codes] }
 * `elapsedSec` = wall-clock length of the take, used to spot dropped audio.
 */
export function analyzeTake(buffer, { elapsedSec = null, calibrated = true } = {}) {
  const { rms, peak, clipped } = windows(buffer);
  const sorted = [...rms].sort((a, b) => a - b);
  const noise = sorted[Math.floor(sorted.length * 0.1)] || 0;
  const loud = rms.filter((v) => v > Math.max(noise * 3, 1e-4));
  const voice = Math.sqrt((loud.length ? loud : rms).reduce((a, v) => a + v * v, 0) / Math.max(1, (loud.length ? loud : rms).length));
  const peakDb = toDb(peak);
  const noiseDb = toDb(noise);
  const issues = [];
  if (peak < 1e-4) issues.push("silent");
  else {
    if (clipped >= 3) issues.push("clipping");
    if (peakDb < -24) issues.push("quiet");
    if (noiseDb > -45 && peakDb - noiseDb < 30) issues.push("noise");
  }
  if (elapsedSec != null && Math.abs(buffer.duration - elapsedSec) > 0.4) issues.push("sync");
  if (!calibrated) issues.push("uncalibrated");
  return { peak_db: round(peakDb), rms_db: round(toDb(voice)), noise_db: round(noiseDb), clipped, issues };
}

/** Advice for the setup level test, from the loudest peak (dBFS) while singing. */
export function levelAdvice(peakDb) {
  if (peakDb == null) return null;
  if (peakDb > -1) return { ok: false, text: "Too loud — it will distort. Step back a little or lower the input gain." };
  if (peakDb < -30) return { ok: false, text: "Very quiet. Move closer to the mic or raise its input gain in your computer's sound settings." };
  if (peakDb < -18) return { ok: true, text: "A bit quiet but usable. Moving a little closer will sound fuller." };
  return { ok: true, text: "Great level — strong and clean." };
}

/** Advice for the quiet-room test, from the noise floor (dBFS). */
export function noiseAdvice(noiseDb) {
  if (noiseDb == null) return null;
  if (noiseDb > -40) return { ok: false, text: "Lots of background noise. Turn off fans / air-con and move away from windows." };
  if (noiseDb > -50) return { ok: true, text: "Some room noise — fine for singing, a quieter room is even better." };
  return { ok: true, text: "Quiet room — excellent." };
}

/** Start times (s) of sharp sounds (clicks) in a buffer — used by sync calibration. */
export function detectOnsets(buffer) {
  const { rms, peak } = windows(buffer, 0.002);
  const sorted = [...rms].sort((a, b) => a - b);
  const floor = sorted[Math.floor(sorted.length * 0.5)] || 0;
  const th = Math.max(floor * 6, peak * 0.3);
  const out = [];
  let quiet = 999;
  rms.forEach((v, i) => {
    if (v >= th && quiet >= 75) out.push(i * 0.002);
    quiet = v >= th ? 0 : quiet + 1;
  });
  return out;
}
